/**
 * @file Zod schemas and TypeScript types for normalising the output
 * from the JRT microservice. These DTOs are used to transform and
 * validate the microservice response before returning it for use
 * throughout the frontend application.
 */
import { z } from 'zod'
import { CoordinatesSchema, VendorsEnum } from '@reward-platform/ancillaries-schemas/common'
import { SupportedLocaleEnum } from '@reward-platform/platform-config'

// Replacing characters that throw 400 errors in JRT services
const sanitiseSearchInput = (val: string) => {
  return val.replaceAll('.', ' ').replaceAll("'", ' ').replaceAll('(', '').replaceAll(')', '')
}

const parseMultiBrandQueryParam = (value: unknown) => {
  if (value === 'true') {
    return true
  }
  if (value === 'false') {
    return false
  }
  return value
}

const MultiBrandQuerySchema = z.preprocess(parseMultiBrandQueryParam, z.boolean().optional())

export const LocationSearchParamsSchema = z
  .object({
    searchString: z.string().transform(sanitiseSearchInput).optional(),
    locale: SupportedLocaleEnum.optional().default('en-US'),
    vendorBrandId: z.coerce.string().optional(),
    multiBrand: MultiBrandQuerySchema,
  })
  .and(CoordinatesSchema.partial())
  .refine(
    (data) => {
      const hasSearchString = !!data.searchString
      const hasCoordinates = data.latitude != null && data.longitude != null

      return hasSearchString || hasCoordinates
    },
    {
      message: 'Either searchString or both latitude and longitude must be provided',
    }
  )

export type LocationSearchParams = z.infer<typeof LocationSearchParamsSchema>

export const LocationOpeningTimesSchema = z.object({
  day: z.string(),
  hours: z.array(z.string()),
})

export type LocationOpeningTimes = z.infer<typeof LocationOpeningTimesSchema>

export const AncillariesLocationSchema = z.object({
  id: z.string(),
  vendorBrandId: z.preprocess((val) => (val === '' ? undefined : val), VendorsEnum.optional()),
  type: z.string(),
  name: z.string(),
  fullName: z.string(),
  countryCode: z.string().optional(),
})

export type AncillariesLocation = z.infer<typeof AncillariesLocationSchema>

/**
 * One car hire brand's desk at a location. Avis and Budget are separate suppliers upstream, each
 * with its own address, phone and opening hours, so a location serving both carries one entry per
 * brand. Every car hire location has this list, whether it serves one brand or two - that is what
 * lets us work out the brands a pick-up and a drop-off have in common.
 */
export const CarHireLocationVendorSchema = z.object({
  brandId: z.preprocess((val) => (val === '' ? undefined : val), VendorsEnum),
  name: z.string().optional(),
  address: z.string().array().optional(),
  emailAddress: z.string().optional(),
  telephoneNumber: z.string().optional(),
  afterHoursIndicator: z.union([z.boolean(), z.string()]).optional(),
  openingTimes: z.array(LocationOpeningTimesSchema).optional(),
  location: z
    .array(z.object({ latitude: z.string().optional(), longitude: z.string().optional() }))
    .optional(),
})

export type CarHireLocationVendor = z.infer<typeof CarHireLocationVendorSchema>

export const CarHireLocationSchema = AncillariesLocationSchema.extend({
  address: z.string().array(),
  emailAddress: z.string().optional(),
  telephoneNumber: z.string().optional(),
  location: z
    .array(z.object({ latitude: z.string().optional(), longitude: z.string().optional() }))
    .optional(),
  openingTimes: z.array(LocationOpeningTimesSchema).optional(),
  afterHoursIndicator: z.union([z.boolean(), z.string()]).optional(),
  vendors: z.array(CarHireLocationVendorSchema).optional(),
})

export type CarHireLocation = z.infer<typeof CarHireLocationSchema>
